/* UPP size guide planner (sections/upp-size-guide).
   All maths in inches; inputs and labels follow the units toggle (ft/in or m/cm).
   Rules (also printed in the static guide):
   - dining: 36 in clearance all round (30 in = tight); chairs drawn per the approved seat mapping
   - coffee: 16-18 in from the sofa, about 2/3 of the sofa length
   - console: its depth plus a 36 in walkway (30 tight); bed: 24 in on the sides and foot (18 tight)
   - desk: 36 in chair space behind (30 tight)
   - TV wall: unit at least as wide as the TV (0.87 x diagonal), 6-12 in of wall each side
   Drawing colours follow the product page's size drawing (assets/upp-pdp.js).
   Product page (snippets/upp-pdp-plan, 2026-10-22): the same planner for one product, loaded when its
   accordion opens. data-sg-fixed: piece / mode / shape come from data-sg-piece, data-sg-mode, data-sg-shape
   (not the URL), no mode or piece buttons, no recommendations; the chip starts on the chosen size
   (data "sel") and follows the buy box (upp:variant-change, data "si" = the Size option's index). */
(function () {
  'use strict';
  var root = document.querySelector('[data-upp-sg]');
  if (!root) return;
  var dataEl = root.querySelector('[data-sg-data]') || document.querySelector('[data-sg-data]');
  var DATA = { products: [] };
  try { DATA = JSON.parse(dataEl.textContent); } catch (e) { /* planner still draws without products */ }

  var form = root.querySelector('[data-sg-form]');
  var svg = root.querySelector('[data-sg-svg]');
  var cap = root.querySelector('[data-sg-cap]');
  var statusEl = root.querySelector('[data-sg-status]');
  var chipsEl = root.querySelector('[data-sg-chips]');
  var recsEl = root.querySelector('[data-sg-recs]');
  var cardsEl = root.querySelector('[data-sg-cards]');
  var noneEl = root.querySelector('[data-sg-none]');
  var cta = root.querySelector('[data-sg-cta]');
  var personSrc = root.getAttribute('data-person-src');
  /* Higgsfield top views (2026-10-17), same as the product page's room drawing: chair image in the chair's footprint,
     walnut texture cropped to each tabletop */
  var chairSrc = root.getAttribute('data-chair-src'), topSrc = root.getAttribute('data-top-src'), clipN = 0;

  var WOOD = '#6B4A2E', WOOD_D = '#3B2716', SEAT = '#2A2420', INK = '#1A1512', FLOOR = '#F3ECE0', WALL = '#442E13';
  var COL = { fit: '#3F7A4A', tight: '#B7791F', big: '#A43324' };
  var CHAIR_W = 18, CHAIR_D = 18, GAP = 2;   /* the walnut dining chair's footprint, as on the product page */
  var PIECE_NAME = { dining: 'Dining table', coffee: 'Coffee table', console: 'Console or side table', bed: 'Bed or nightstand', desk: 'Desk' };

  var state = { mode: 'room', piece: 'dining', shape: 'rect', metric: root.getAttribute('data-metric') === 'true', chip: null };
  var fixed = root.hasAttribute('data-sg-fixed');
  var parquetSrc = root.getAttribute('data-parquet-src');

  /* ---------- units ---------- */
  var r1 = function (v) { return Math.round(v * 10) / 10; };
  function roomToIn(v) { return state.metric ? v * 100 / 2.54 : v * 12; }        /* m or ft -> in */
  function inToRoom(v) { return state.metric ? r1(v * 2.54 / 100) : r1(v / 12); }
  function smallToIn(v) { return state.metric ? v / 2.54 : v; }                   /* cm or in -> in */
  function inToSmall(v) { return state.metric ? Math.round(v * 2.54) : Math.round(v); }
  function len(v) { return state.metric ? Math.round(v * 2.54) + ' cm' : Math.round(v) + ' in'; }
  function roomLen(v) {
    if (state.metric) return (Math.round(v * 2.54) / 100).toFixed(2).replace(/0$/, '') + ' m';
    var ft = Math.floor(v / 12), inch = Math.round(v - ft * 12);
    if (inch === 12) { ft += 1; inch = 0; }
    return ft + ' ft' + (inch ? ' ' + inch + ' in' : '');
  }
  function sizeLabel(L, D, round) { return D ? len(L).replace(/ (in|cm)$/, '') + ' × ' + len(D) : len(L) + (round ? ' round' : ' long'); }

  var DEFAULTS = { roomL: 168, roomW: 144, sofa: 84, wall: 120, ceiling: 96 };   /* inches */
  function field(name) { return form.elements[name]; }
  function num(name) { var f = field(name), v = f ? parseFloat(f.value) : NaN; return isFinite(v) && v > 0 ? v : null; }   /* the product page only has its piece's fields */
  function setUnits(convertFrom) {
    root.querySelectorAll('[data-sg-unit="room"]').forEach(function (el) { el.textContent = state.metric ? '(m)' : '(ft)'; });
    root.querySelectorAll('[data-sg-unit="small"]').forEach(function (el) { el.textContent = state.metric ? '(cm)' : '(in)'; });
    form.querySelectorAll('[data-sg-num]').forEach(function (inp) {
      var isRoom = inp.getAttribute('data-sg-num') === 'room';
      inp.step = isRoom ? (state.metric ? '0.05' : '0.5') : '1';
      var v = parseFloat(inp.value);
      var inches;
      if (isFinite(v) && v > 0 && convertFrom !== undefined) {
        var wasMetric = convertFrom;
        inches = isRoom ? (wasMetric ? v * 100 / 2.54 : v * 12) : (wasMetric ? v / 2.54 : v);
      } else if (!(isFinite(v) && v > 0) && inp.name !== 'ceiling') {
        inches = DEFAULTS[inp.name];
      }
      if (inches !== undefined) inp.value = isRoom ? inToRoom(inches) : inToSmall(inches);
    });
  }

  /* ---------- data ---------- */
  function sizesOf(kind) {
    var out = [];
    DATA.products.forEach(function (p, order) {
      if (p.k !== kind) return;
      p.v.forEach(function (v) {
        var a = v[1], b = v[2], c = v[3];
        var L = Math.max(a, b), D = Math.min(a, b);
        if (kind === 'dining' && p.r && (!b || Math.abs(a - b) < 0.5)) { L = a; D = 0; }
        if (!D && !(kind === 'dining' && p.r) && kind !== 'console') return;   /* footprint needs two numbers */
        out.push({ p: p, order: order, id: v[0], L: L, D: D, H: c || 0, seats: v[4] || 0, round: kind === 'dining' && !!p.r && !D, val: v[5] });
      });
    });
    return out;
  }
  /* dining sizes of the chosen shape only: Rectangle (default) or Round (owner 2026-10-17) */
  function byShape(list) { return list.filter(function (s) { return state.shape === 'round' ? s.round : !s.round; }); }
  function seatsFor(s) {
    if (s.seats) return s.seats;
    if (s.round) return s.L < 42 ? 4 : s.L < 54 ? 4 : s.L < 60 ? 6 : 6;
    var l = s.L;
    return l < 54 ? 4 : l < 66 ? 6 : l < 70 ? 6 : l < 78 ? 8 : l < 88 ? 8 : 10;
  }

  /* ---------- fit rules ---------- */
  function fitRoom(kind, s, room) {
    var RL = Math.max(room.L, room.W), RW = Math.min(room.L, room.W);
    var D = s.D || 16, res;
    var grade = function (needL, needW, tightL, tightW) {
      if (s.L + needL <= RL && D + needW <= RW) return 'fit';
      if (s.L + tightL <= RL && D + tightW <= RW) return 'tight';
      return 'big';
    };
    switch (kind) {
      case 'dining':
        if (s.round) {
          var g = s.L + 72 <= RW ? 'fit' : s.L + 60 <= RW ? 'tight' : 'big';
          res = { s: g, why: g === 'fit' ? 'Leaves ' + len(36) + ' for chairs and a walkway all round.' : g === 'tight' ? 'Under ' + len(36) + ' around the table: chairs fit, the walkway is tight.' : 'Less than ' + len(30) + ' around the table: too big for this room.' };
        } else {
          var gd = grade(72, 72, 60, 60);
          res = { s: gd, why: gd === 'fit' ? 'Leaves ' + len(36) + ' for chairs and a walkway on every side.' : gd === 'tight' ? 'Between ' + len(30).replace(/ (in|cm)$/, '') + ' and ' + len(36) + ' on a side: it fits, but the walkway is tight.' : 'Less than ' + len(30) + ' on a side: too big for this room.' };
        }
        break;
      case 'coffee':
        var sofa = room.sofa || 84, ratio = s.L / sofa;
        var depthOk = 36 + 18 + D + 30 <= RW;
        var gc = ratio > 1 || !depthOk ? 'big' : (ratio >= 0.5 && ratio <= 0.75) ? 'fit' : 'tight';
        res = { s: gc, why: !depthOk ? 'With the sofa and ' + len(16).replace(/ (in|cm)$/, '') + '–' + len(18) + ' in front of it, there is no walkway left.' : ratio > 1 ? 'Longer than the sofa.' : gc === 'fit' ? 'About two thirds of the sofa length: a balanced fit.' : ratio < 0.5 ? 'Short for this sofa: about two thirds of its length looks best.' : 'Long for this sofa: about two thirds of its length looks best.' };
        break;
      case 'console':
        var gk = s.L + 12 <= RL && D + 36 <= RW ? 'fit' : s.L <= RL && D + 30 <= RW ? 'tight' : 'big';
        res = { s: gk, why: gk === 'fit' ? 'Leaves a ' + len(36) + ' walkway in front.' : gk === 'tight' ? 'Fits, with a walkway under ' + len(36) + ' or little wall either side.' : 'Too long for the wall or no walkway left.' };
        break;
      case 'bed':
        var bw = D, bl = s.L;
        var gb = bw + 48 <= RL && bl + 24 <= RW ? 'fit' : bw + 36 <= RL && bl + 18 <= RW ? 'tight' : 'big';
        res = { s: gb, why: gb === 'fit' ? 'Leaves ' + len(24) + ' on both sides and at the foot.' : gb === 'tight' ? 'Under ' + len(24) + ' on a side or at the foot.' : 'Too big for this room.' };
        break;
      default:
        var gw = s.L + 12 <= RL && D + 36 <= RW ? 'fit' : s.L <= RL && D + 30 <= RW ? 'tight' : 'big';
        res = { s: gw, why: gw === 'fit' ? 'Leaves ' + len(36) + ' behind the desk for the chair.' : gw === 'tight' ? 'Under ' + len(36) + ' behind the desk for the chair.' : 'Too big for this room.' };
    }
    return res;
  }
  function fitWall(s, wall, tvW) {
    var margin = (wall - s.L) / 2;
    if (s.L > wall) return { s: 'big', why: 'Wider than the wall.' };
    if (margin < 6) return { s: 'tight', why: 'Less than ' + len(6) + ' of wall on each side.' };
    if (s.L < tvW) return { s: 'tight', why: 'Narrower than the TV (' + len(tvW) + ' wide).' };
    return { s: 'fit', why: margin <= 12 ? 'At least as wide as the TV, with ' + len(6).replace(/ (in|cm)$/, '') + '–' + len(12) + ' of wall each side.' : 'At least as wide as the TV, with room to spare on the wall.' };
  }

  /* ---------- drawing helpers ---------- */
  var out, U = 1;
  function el(tag, attrs, inner) {
    var a = '';
    for (var k in attrs) if (attrs[k] !== undefined && attrs[k] !== null) a += ' ' + k + '="' + attrs[k] + '"';
    out.push('<' + tag + a + (inner === undefined ? '/>' : '>' + inner + '</' + tag + '>'));
  }
  function label(x, y, text, opts) {
    opts = opts || {};
    el('text', { x: r1(x), y: r1(y), 'text-anchor': opts.anchor || 'middle', class: 'upp-sg__lbl' + (opts.cls ? ' ' + opts.cls : ''),
      transform: opts.rot ? 'rotate(' + opts.rot + ' ' + r1(x) + ' ' + r1(y) + ')' : null, 'font-size': opts.size }, text);
  }
  function chair(cx, cy, angle, u) {
    var w = CHAIR_W, d = CHAIR_D;
    out.push('<g transform="translate(' + r1(cx) + ' ' + r1(cy) + ') rotate(' + angle + ')">' + (chairSrc
      ? '<image href="' + chairSrc + '" x="' + (-w / 2) + '" y="' + (-d / 2) + '" width="' + w + '" height="' + d + '" preserveAspectRatio="none"/>'
      : '<rect x="' + (-w / 2) + '" y="' + (-d / 2) + '" width="' + w + '" height="' + d + '" rx="' + r1(d * 0.2) + '" fill="' + SEAT + '" stroke="' + WOOD + '" stroke-width="' + r1(1.2 * u) + '"/>' +
        '<line x1="' + (-w / 2 + 1) + '" y1="' + (-d / 2) + '" x2="' + (w / 2 - 1) + '" y2="' + (-d / 2) + '" stroke="' + WOOD + '" stroke-width="' + r1(2.6 * u) + '" stroke-linecap="round"/>') + '</g>');
  }
  /* a walnut top: colour first (shows while the texture loads), texture clipped to the shape, outline on top.
     tag 'rect' or 'circle'; attrs as for el(); box = the shape's bounds */
  function woodTop(tag, attrs, bx, by, bw, bh) {
    el(tag, Object.assign({}, attrs, { fill: WOOD }));
    if (topSrc) {
      var id = 'uppSgTop' + (++clipN), shape = [];
      for (var k in attrs) shape.push(k + '="' + attrs[k] + '"');
      out.push('<clipPath id="' + id + '"><' + tag + ' ' + shape.join(' ') + '/></clipPath>');
      var cx = bx + bw / 2, cy = by + bh / 2, rot = bh > bw * 1.2;   /* grain along the long side */
      el('image', rot
        ? { href: topSrc, x: r1(cx - bh / 2), y: r1(cy - bw / 2), width: r1(bh), height: r1(bw), preserveAspectRatio: 'xMidYMid slice', transform: 'rotate(90 ' + r1(cx) + ' ' + r1(cy) + ')', 'clip-path': 'url(#' + id + ')' }
        : { href: topSrc, x: r1(bx), y: r1(by), width: r1(bw), height: r1(bh), preserveAspectRatio: 'xMidYMid slice', 'clip-path': 'url(#' + id + ')' });
    }
    el(tag, Object.assign({}, attrs, { fill: 'none', stroke: WOOD_D, 'stroke-width': r1(1.2 * U) }));
  }
  function zone(x, y, w, h, status, rx) {
    el('rect', { x: r1(x), y: r1(y), width: r1(w), height: r1(h), rx: rx || 0, fill: COL[status], 'fill-opacity': 0.12, stroke: COL[status], 'stroke-width': r1(1.4 * U), 'stroke-dasharray': '6 4', class: 'upp-sg__zone' });
  }

  /* ---------- room plan ---------- */
  function drawRoom(kind, s, room, fit) {
    out = [];
    var RL = Math.max(room.L, room.W), RW = Math.min(room.L, room.W);
    /* one scale for rooms up to 20 x 15 ft, so a smaller room shows smaller on the floor (owner 2026-10-17);
       bigger rooms widen the view. The room is parquet with walnut walls; no frame around it. */
    var EL = Math.max(RL, 240), EW = Math.max(RW, 180);
    var pad = EL * 0.08 + 14;
    var u = EL / 300;                                     /* stroke unit, ~1 px at full width */
    U = u;
    svg.setAttribute('viewBox', [-pad, -pad, EL + pad * 2, EW + pad * 2].map(r1).join(' '));
    svg.style.setProperty('--sg-font', r1(11 * u) + 'px');
    if (parquetSrc) out.push('<defs><pattern id="sgParquet" patternUnits="userSpaceOnUse" width="293" height="98"><image href="' + parquetSrc + '" width="293" height="98" preserveAspectRatio="none"/></pattern></defs>');
    out.push('<g transform="translate(' + r1((EL - RL) / 2) + ' ' + r1((EW - RW) / 2) + ')">');
    el('rect', { x: 0, y: 0, width: RL, height: RW, fill: parquetSrc ? 'url(#sgParquet)' : FLOOR, stroke: WALL, 'stroke-width': r1(4 * u) });
    /* room dimension labels */
    label(RL / 2, -pad * 0.35, roomLen(RL));
    label(-pad * 0.35, RW / 2, roomLen(RW), { rot: -90 });
    var cx = RL / 2, cy = RW / 2, D = s ? (s.D || 16) : 0, st = fit ? fit.s : 'fit';
    if (!s) { svg.innerHTML = out.join('') + '</g>'; return; }
    if (kind === 'dining') {
      if (s.round) {
        var R = s.L / 2;
        el('circle', { cx: cx, cy: cy, r: r1(R + 36), fill: COL[st], 'fill-opacity': 0.12, stroke: COL[st], 'stroke-width': r1(1.4 * u), 'stroke-dasharray': '6 4' });
        var n = seatsFor(s);
        for (var i = 0; i < n; i++) {
          var ang = -90 + 360 * i / n, rad = ang * Math.PI / 180, dist = R + GAP + CHAIR_D / 2;
          chair(cx + Math.cos(rad) * dist, cy + Math.sin(rad) * dist, ang + 90, u);
        }
        woodTop('circle', { cx: cx, cy: cy, r: r1(R) }, cx - R, cy - R, 2 * R, 2 * R);
        label(cx, cy + 4 * u, len(s.L), { cls: 'upp-sg__lbl--on' });
      } else {
        var L = s.L, tx = cx - L / 2, ty = cy - D / 2;
        zone(tx - 36, ty - 36, L + 72, D + 72, st, 4);
        /* same rule as the product page (assets/upp-pdp.js chairLayout): one chair per end from 4 chairs if the end fits it */
        var seats = seatsFor(s), ends = seats >= 4 && D >= CHAIR_W + 4 ? 1 : 0, sides = Math.max(0, seats - 2 * ends);
        var topN = Math.ceil(sides / 2), botN = Math.floor(sides / 2);
        var along = function (k, N) { var span = N * CHAIR_W <= L ? L : N * (CHAIR_W + 1); return cx - span / 2 + span * (k + 0.5) / N; };
        for (var k = 0; k < topN; k++) chair(along(k, topN), ty - GAP - CHAIR_D / 2, 0, u);
        for (k = 0; k < botN; k++) chair(along(k, botN), ty + D + GAP + CHAIR_D / 2, 180, u);
        if (ends) { chair(tx - GAP - CHAIR_D / 2, cy, -90, u); chair(tx + L + GAP + CHAIR_D / 2, cy, 90, u); }
        woodTop('rect', { x: r1(tx), y: r1(ty), width: r1(L), height: r1(D), rx: 1 }, tx, ty, L, D);
        label(cx, cy + 4 * u, sizeLabel(L, D, false), { cls: 'upp-sg__lbl--on' });
      }
      label(cx, RW + pad * 0.62, seatsFor(s) + ' seats · dashed line: ' + len(36) + ' for chairs and a walkway', { cls: 'upp-sg__lbl--sm' });
    } else if (kind === 'coffee') {
      var sofa = room.sofa || 84, sx = cx - sofa / 2, sy = RW - 36;
      el('rect', { x: r1(sx), y: r1(sy), width: r1(sofa), height: 36, rx: 4, fill: '#D9CFBF', stroke: '#8A7B66', 'stroke-width': r1(1.2 * u) });
      label(cx, sy + 22, 'Sofa ' + len(sofa));   /* ink, not the small bark label: bark on the beige sofa was 3.8:1 (review 2026-10-18) */
      var ty2 = sy - 17 - D;
      zone(cx - s.L / 2, ty2 + D, s.L, 17, st, 0);
      woodTop('rect', { x: r1(cx - s.L / 2), y: r1(ty2), width: r1(s.L), height: r1(D), rx: r1(Math.min(D, s.L) / 2.4) }, cx - s.L / 2, ty2, s.L, D);
      label(cx, ty2 - 6 * u, sizeLabel(s.L, D));
      label(cx, -pad * 0.35 + RW + pad * 0.95, '' + len(16).replace(/ (in|cm)$/, '') + '–' + len(18) + ' between sofa and table · about ⅔ of the sofa length', { cls: 'upp-sg__lbl--sm' });
    } else {
      /* console, bed and desk stand against the top wall */
      var w = kind === 'bed' ? D : s.L, d = kind === 'bed' ? s.L : D, x0 = cx - w / 2;
      if (kind === 'bed') zone(x0 - 24, 0, w + 48, d + 24, st, 0);
      else zone(x0, d, w, 36, st, 0);
      if (kind === 'desk') chair(cx, d + GAP + CHAIR_D / 2 + 4, 180, u);
      if (kind === 'bed') el('rect', { x: r1(x0), y: 0, width: r1(w), height: r1(d), rx: 1, fill: '#E8DFD0', stroke: WOOD_D, 'stroke-width': r1(1.2 * u) });
      else woodTop('rect', { x: r1(x0), y: 0, width: r1(w), height: r1(d), rx: 1 }, x0, 0, w, d);
      if (kind === 'bed') el('rect', { x: r1(x0), y: 0, width: r1(w), height: r1(Math.min(8, d / 6)), fill: WOOD, stroke: WOOD_D, 'stroke-width': r1(1.2 * u) });
      label(cx, d + (kind === 'bed' ? 24 : 36) + 12 * u, sizeLabel(s.L, s.D || 0, false));
      var rule = kind === 'bed' ? 'dashed line: ' + len(24) + ' on both sides and at the foot' : kind === 'desk' ? 'dashed area: ' + len(36) + ' behind the desk for the chair' : 'dashed area: a ' + len(36) + ' walkway in front';
      label(cx, RW + pad * 0.62, rule, { cls: 'upp-sg__lbl--sm' });
    }
    svg.innerHTML = out.join('') + '</g>';
  }

  /* ---------- wall view ---------- */
  function drawWall(s, wall, ceiling, tv, fit) {
    out = [];
    var H = ceiling || Math.max(96, 66.9 + 20), tvW = tv * 0.87, tvH = tv * 0.49;
    var pad = 16, left = 0;   /* no person on the wall (owner 2026-10-18) */
    var vbX = -left - pad, vbW = wall + left + pad * 2, vbH = H + pad * 2 + 20;
    var u = vbW / 300;
    U = u;
    svg.setAttribute('viewBox', [vbX, -pad, vbW, vbH].map(r1).join(' '));
    svg.style.setProperty('--sg-font', r1(11 * u) + 'px');
    el('rect', { x: 0, y: 0, width: r1(wall), height: r1(H), fill: FLOOR, stroke: ceiling ? WALL : 'none', 'stroke-width': r1(2 * u) });
    el('line', { x1: r1(vbX), y1: r1(H), x2: r1(wall + pad), y2: r1(H), stroke: WALL, 'stroke-width': r1(3 * u) });
    if (!ceiling) label(wall / 2, 10 * u, 'Ceiling height not entered', { cls: 'upp-sg__lbl--sm' });
    label(wall / 2, H + 16 * u, 'Wall ' + roomLen(wall));
    var uh = s ? (s.H || 30) : 30, uw = s ? s.L : 0, cx = wall / 2;
    if (s) {
      var st = fit ? fit.s : 'fit';
      if (uw <= wall) {
        zone(0, H - uh, (wall - uw) / 2, uh, st, 0);
        zone(cx + uw / 2, H - uh, (wall - uw) / 2, uh, st, 0);
      }
      el('rect', { x: r1(cx - uw / 2), y: r1(H - uh), width: r1(uw), height: r1(uh), rx: 1, fill: WOOD, stroke: WOOD_D, 'stroke-width': r1(1.2 * u) });
      label(cx, H - uh / 2 + 4 * u, len(uw) + (s.H ? ' × ' + len(s.H) + ' high' : ''), { cls: 'upp-sg__lbl--on' });
    }
    var tvBottom = H - uh - 8;
    el('rect', { x: r1(cx - tvW / 2), y: r1(tvBottom - tvH), width: r1(tvW), height: r1(tvH), rx: 1.5, fill: '#1F1C1A', stroke: '#000', 'stroke-width': r1(1 * u) });
    var tvText = tv + ' in TV · ' + len(tvW) + ' wide';
    /* inside the screen when it fits (about 0.55 em per character), otherwise above it */
    if (tvText.length * 11 * u * 0.55 < tvW - 8 * u) label(cx, tvBottom - tvH / 2 + 4 * u, tvText, { cls: 'upp-sg__lbl--on' });
    else label(cx, tvBottom - tvH - 6 * u, tvText);
    svg.innerHTML = out.join('');
  }

  /* ---------- chips ---------- */
  /* keep: only the chosen chip changed, so leave the radios in place and keyboard focus on the group (review 2026-10-18) */
  function renderChips(list, fitFn, keep) {
    var seen = {}, chips = [];
    list.forEach(function (s) {
      var key = Math.round(s.L) + 'x' + Math.round(s.D);
      if (seen[key]) return;
      seen[key] = true;
      chips.push({ key: key, s: s, fit: fitFn(s) });
    });
    chips.sort(function (a, b) { return a.s.L - b.s.L || a.s.D - b.s.D; });
    if (keep) return chips;
    /* the grade is only a dot colour on screen, so screen readers get it in the radio's name */
    var GRADE = { fit: 'fits', tight: 'tight', big: 'too big' };
    var html = chips.map(function (c) {
      var name = sizeLabel(c.s.L, c.s.D, c.s.round);
      return '<label class="upp-sg__chip is-' + c.fit.s + '"><input type="radio" name="sg-size" value="' + c.key + '" aria-label="' + name + ', ' + GRADE[c.fit.s] + '"' + (c.key === state.chip ? ' checked' : '') + '><span>' + name + '</span></label>';
    }).join('');
    var hadFocus = document.activeElement && document.activeElement.name === 'sg-size' && chipsEl.contains(document.activeElement);
    chipsEl.innerHTML = html || '<p class="upp-sg__empty">No sizes to show.</p>';
    if (hadFocus) {
      var again = chipsEl.querySelector('input[value="' + state.chip + '"]') || chipsEl.querySelector('input');
      if (again) again.focus();
    }
    return chips;
  }
  function pickChip(chips) {
    var cur = chips.filter(function (c) { return c.key === state.chip; })[0];
    if (cur) return cur;
    var fits = chips.filter(function (c) { return c.fit.s === 'fit'; });
    var tight = chips.filter(function (c) { return c.fit.s === 'tight'; });
    var pick = fits[fits.length - 1] || tight[tight.length - 1] || chips[0];
    if (pick) {
      state.chip = pick.key;
      var inp = chipsEl.querySelector('input[value="' + pick.key + '"]');
      if (inp) inp.checked = true;
    }
    return pick;
  }

  /* ---------- recommendations ---------- */
  var cardCache = {}, recToken = 0;
  function recommend(list, fitFn, filter, prefer) {
    var best = {};
    list.forEach(function (s) {
      if (filter && !filter(s)) return;
      var f = fitFn(s);
      if (f.s === 'big') return;
      var cur = best[s.p.h], rank = f.s === 'fit' ? 2 : 1;
      var bigger = s.L * (s.D || s.L) > (cur ? cur.s.L * (cur.s.D || cur.s.L) : 0);
      if (!cur || rank > cur.rank || (rank === cur.rank && (prefer === 'small' ? !bigger : bigger))) best[s.p.h] = { s: s, rank: rank };
    });
    var picks = Object.keys(best).map(function (h) { return best[h]; });
    picks.sort(function (a, b) {
      return b.rank - a.rank || (prefer === 'small' ? seatsFor(a.s) - seatsFor(b.s) : 0) || a.s.order - b.s.order;
    });
    return picks.slice(0, 8);
  }
  function showRecs(picks) {
    if (!recsEl) return;   /* product page: no recommendations */
    var token = ++recToken;
    recsEl.hidden = false;
    noneEl.hidden = picks.length > 0;
    if (!picks.length) { cardsEl.innerHTML = ''; return; }
    var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    Promise.all(picks.map(function (pk) {
      var h = pk.s.p.h;
      if (cardCache[h]) return Promise.resolve(cardCache[h]);
      return fetch(base + 'products/' + encodeURIComponent(h) + '?section_id=upp-card-fragment', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (t) {
          var m = document.createElement('div'); m.innerHTML = t;
          var card = m.querySelector('.upp-card');
          cardCache[h] = card ? card.outerHTML : '';
          return cardCache[h];
        })
        .catch(function () { return ''; });
    })).then(function (htmls) {
      if (token !== recToken) return;
      cardsEl.innerHTML = htmls.map(function (html, i) {
        if (!html) return '';
        var pk = picks[i];
        var tag = pk.rank === 2 ? '' : '<span class="upp-sg__tag is-tight">Tight fit</span>';
        return '<li class="upp-sg__card" data-variant="' + pk.s.id + '">' + html + '<p class="upp-sg__fitline">' + tag + 'Fits: ' + sizeLabel(pk.s.L, pk.s.D, pk.s.round) + (pk.s.p.k === 'dining' ? ' · ' + seatsFor(pk.s) + ' seats' : '') + '</p></li>';
      }).join('');
      cardsEl.querySelectorAll('[data-variant]').forEach(function (li) {
        var vid = li.getAttribute('data-variant');
        li.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
          try { var u = new URL(a.href, location.origin); u.searchParams.set('variant', vid); a.href = u.pathname + u.search; } catch (e) {}
        });
      });
    });
  }

  /* ---------- main update ---------- */
  function readRoom() {
    var L = num('roomL'), W = num('roomW');
    return { L: L ? roomToIn(L) : DEFAULTS.roomL, W: W ? roomToIn(W) : DEFAULTS.roomW, sofa: num('sofa') ? smallToIn(num('sofa')) : DEFAULTS.sofa };
  }
  function setStatus(fit, text) {
    statusEl.className = 'upp-sg__status' + (fit ? ' is-' + fit.s : '');
    statusEl.innerHTML = fit ? '<strong>' + (fit.s === 'fit' ? 'Fits' : fit.s === 'tight' ? 'Tight' : 'Too big') + ':</strong> ' + text : text;
  }
  function setCta(piece) {
    if (!cta) return;
    var b = cta.getAttribute('data-base') || '/pages/custom-furniture';
    cta.href = b + (b.indexOf('?') > -1 ? '&' : '?') + 'piece=' + encodeURIComponent(PIECE_NAME[piece] || 'Dining table');
  }
  function showFields() {
    root.querySelectorAll('[data-sg-show]').forEach(function (el2) {
      var modes = el2.getAttribute('data-sg-show').split(' ');
      var only = el2.getAttribute('data-sg-only');
      el2.hidden = modes.indexOf(state.mode) < 0 || (only && state.mode === 'room' && only !== state.piece);
    });
  }
  function update(keepChips) {
    showFields();
    if (state.mode === 'room') {
      var room = readRoom(), list = sizesOf(state.piece);
      if (state.piece === 'dining' && !fixed) list = byShape(list);   /* one product: draw each of its sizes as it is */
      var fitFn = function (s) { return fitRoom(state.piece, s, room); };
      var chips = renderChips(list, fitFn, keepChips);
      var pick = pickChip(chips);
      drawRoom(state.piece, pick ? pick.s : null, room, pick ? pick.fit : null);
      cap.textContent = 'Room plan to scale: ' + roomLen(Math.max(room.L, room.W)) + ' × ' + roomLen(Math.min(room.L, room.W)) + (pick ? ', ' + (PIECE_NAME[state.piece] || PIECE_NAME.dining).split(' or ')[0].toLowerCase() + ' ' + sizeLabel(pick.s.L, pick.s.D, pick.s.round) : '');
      if (pick) setStatus(pick.fit, sizeLabel(pick.s.L, pick.s.D, pick.s.round) + '. ' + pick.fit.why);
      else setStatus(null, 'Enter your room to see which of our sizes fit.');
      showRecs(recommend(list, fitFn));
      setCta(state.piece);
    } else if (state.mode === 'wall') {
      var wall = num('wall') ? roomToIn(num('wall')) : DEFAULTS.wall;
      var ceil = num('ceiling') ? roomToIn(num('ceiling')) : 0;
      var tv = Math.max(20, Math.min(120, parseFloat(field('tv').value) || 65)), tvW = tv * 0.87;
      var units = sizesOf('console');
      var wfit = function (s) { return fitWall(s, wall, tvW); };
      var chips2 = renderChips(units, wfit, keepChips);
      var pick2 = pickChip(chips2);
      drawWall(pick2 ? pick2.s : null, wall, ceil, tv, pick2 ? pick2.fit : null);
      cap.textContent = 'Wall to scale: ' + roomLen(wall) + ' wide, ' + tv + ' in TV' + (pick2 ? ', unit ' + len(pick2.s.L) + ' wide' : '');
      if (pick2) setStatus(pick2.fit, len(pick2.s.L) + ' wide' + (pick2.s.H ? '' : ' (height not listed, drawn at ' + len(30) + ')') + '. ' + pick2.fit.why);
      else setStatus(null, 'Enter your wall width to see which units fit.');
      showRecs(recommend(units, wfit));
      setCta('console');
    } else {
      var room3 = readRoom(), people = parseInt(field('people').value, 10) || 0;
      var dl = byShape(sizesOf('dining'));
      var f3 = function (s) { return fitRoom('dining', s, room3); };
      var want = function (s) { return !people || seatsFor(s) >= people; };
      var picks = recommend(dl, f3, want, 'small');
      var top = picks[0];
      drawRoom('dining', top ? top.s : null, room3, top ? f3(top.s) : null);
      cap.textContent = 'Room plan to scale: ' + roomLen(Math.max(room3.L, room3.W)) + ' × ' + roomLen(Math.min(room3.L, room3.W)) + (top ? ', best fit ' + sizeLabel(top.s.L, top.s.D, top.s.round) : '');
      /* no table of this shape seats that many: say so instead of blaming the room (review 2026-10-18) */
      var most = dl.reduce(function (m, s) { return Math.max(m, seatsFor(s)); }, 0);
      var seatsShort = !top && people && dl.length && most < people;
      if (top) setStatus(f3(top.s), top.s.p.t + ', ' + sizeLabel(top.s.L, top.s.D, top.s.round) + ', ' + seatsFor(top.s) + ' seats.');
      else if (seatsShort) setStatus(null, 'Our ' + (state.shape === 'round' ? 'round' : 'rectangular') + ' tables seat up to ' + most + '. For ' + people + ', try ' + (state.shape === 'round' ? 'a rectangular table or ' : '') + 'a custom size.');
      else setStatus({ s: 'big' }, 'None of our tables' + (people ? ' for ' + people : '') + ' leave ' + len(30) + ' around them in this room.');
      showRecs(picks);
      if (seatsShort && recsEl) recsEl.hidden = true;   /* its empty text says the room is too small */
      setCta('dining');
    }
  }

  var timer;
  function later() { clearTimeout(timer); timer = setTimeout(update, 250); }
  form.addEventListener('input', function (e) {
    if (e.target.name === 'sg-size') return;
    /* a new room size re-picks the best fitting size unless the visitor chose one */
    if (e.target.matches && e.target.matches('[data-sg-num]') && !state.chipUser) state.chip = null;
    later();
  });
  form.addEventListener('change', function (e) {
    var t = e.target;
    if (t.name === 'sg-mode') { state.mode = t.value; state.chip = null; state.chipUser = false; update(); }
    else if (t.name === 'piece' && PIECE_NAME[t.value]) { state.piece = t.value; state.chip = null; state.chipUser = false; update(); }
    else if (t.name === 'shape') { state.shape = t.value === 'round' ? 'round' : 'rect'; state.chip = null; state.chipUser = false; update(); }
    else if (t.name === 'units') { var was = state.metric; state.metric = t.value === 'metric'; setUnits(was); update(); }
    else if (t.name === 'sg-size') { state.chip = t.value; state.chipUser = true; update(true); }
    else later();
  });

  /* ?piece=dining|coffee|console|bed|desk or ?mode=wall|recommend opens that view; the product page sets them on the root */
  var q = new URLSearchParams(fixed ? '' : location.search);
  if (fixed) {
    if (PIECE_NAME[root.getAttribute('data-sg-piece')]) state.piece = root.getAttribute('data-sg-piece');
    if (root.getAttribute('data-sg-mode') === 'wall') state.mode = 'wall';
    if (root.getAttribute('data-sg-shape') === 'round') state.shape = 'round';
    /* the chip of the size chosen in the buy box: kept when the room changes, like a chip the visitor picked */
    var chipOf = function (val) {
      var s = sizesOf(state.mode === 'wall' ? 'console' : state.piece).filter(function (x) { return x.val === val; })[0];
      return s ? Math.round(s.L) + 'x' + Math.round(s.D) : null;
    };
    var first = chipOf(DATA.sel);
    if (first) { state.chip = first; state.chipUser = true; }
    document.addEventListener('upp:variant-change', function (e) {
      var v = e.detail && e.detail.variant, k = v && v.options && chipOf(v.options[DATA.si]);
      if (k && k !== state.chip) { state.chip = k; state.chipUser = true; update(); }
    });
  }
  if (q.get('piece') && PIECE_NAME[q.get('piece')]) { state.piece = q.get('piece'); field('piece').value = state.piece; }
  if (q.get('shape') === 'round') { state.shape = 'round'; field('shape').value = 'round'; }
  if (q.get('mode') === 'wall' || q.get('mode') === 'recommend') {
    state.mode = q.get('mode');
    var mr = form.querySelector('input[name="sg-mode"][value="' + state.mode + '"]');
    if (mr) mr.checked = true;
  }
  setUnits();
  update();
  root.classList.add('is-ready');
})();
